import { AfterViewInit, Component, ElementRef, OnDestroy, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { Script } from '../../../service/script.service';
import { AdminScript, AdminService } from '../../../service/admin.service';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { SearchInputComponent } from "../../../helper/search-input/search-input.component";
import { MatDivider } from "@angular/material/divider";
import { MatListModule } from "@angular/material/list";
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScriptCardComponent } from '../../../helper/script-card/script-card.component';

@Component({
  selector: 'app-scripts',
  imports: [
    MatCardModule, 
    MatChipsModule, 
    MatIconModule, 
    SearchInputComponent, 
    MatDivider, 
    MatListModule, 
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatMenuModule,
    ScriptCardComponent
  ],
  templateUrl: './scripts.component.html',
  styleUrl: './scripts.component.less'
})
export class ScriptsComponent implements AfterViewInit, OnDestroy {

  adminService = inject(AdminService);
  snackBar = inject(MatSnackBar);

  scripts: AdminScript[] = [];



  page = 1;
  pageSize = 20;
  currentQuery = '';
  filters: number = 0;
  includeDrafts: boolean = false;
  sortBy: string = 'id';
  sortOrder: string = 'desc';

  // Script counts for each status
  scriptCounts = {
    pending: 0,
    pending_update: 0,
    rejected: 0,
    published: 0,
    previously_published: 0
  };

  isLoading = false;
  isInitialLoading = false;
  hasMoreData = true;

  @ViewChildren('scriptItem', { read: ElementRef }) scriptItems!: QueryList<ElementRef<HTMLElement>>;
  private intersectionObserver?: IntersectionObserver;

  ngOnInit() {
    this.loadState();
    this.isInitialLoading = true;
    this.loadScripts(true);
  }

  private debounceTimer: any;

  uploadScripts() {
    this.snackBar.open("Uploading scripts...", "Close", { duration: 300000 });
    this.adminService.uploadScripts().subscribe((response) => {
      this.snackBar.open(response.message, "Close", { duration: 3000 });
    });
  }

  onSearchInput(query: string) {
    // Clear any existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set a new timer for 500ms debounce
    this.debounceTimer = setTimeout(() => {
      this.currentQuery = query;
      this.page = 1;
      this.hasMoreData = true;
      this.isInitialLoading = true;
      this.loadScripts(true);
    }, 500);
  }
  

  search(query: string) {
    this.currentQuery = query;
    this.page = 1;
    this.hasMoreData = true;
    this.isInitialLoading = true;
    this.saveState();
    this.loadScripts(true);
  }

  loadScripts(reset: boolean = false) {
    if (this.isLoading || (!this.hasMoreData && !reset)) return;
    this.isLoading = true;
    this.adminService.getScripts(this.currentQuery, this.page, this.filters, this.includeDrafts, this.sortBy, this.sortOrder).subscribe((response) => {
      this.isLoading = false;
      this.isInitialLoading = false;
      const newScripts = response.data.scripts;
      this.pageSize = (response as any).page_size ?? this.pageSize;
      
      // Update script counts
      this.scriptCounts = response.data.counts;
      
      if (reset) {
        this.scripts = newScripts;
      } else {
        this.scripts = [...this.scripts, ...newScripts];
      }

      if (newScripts.length < this.pageSize) {
        this.hasMoreData = false;
      }

      this.page++;
    });
  }

  getChips(flags: number, isDraft?: boolean) {
    let chips: {title: string, icon: string}[] = [];

    if (isDraft) {
      chips.push({title: "Draft", icon: "edit"});
    }

    if (flags & 4) {
      chips.push({title: "Rejected", icon: "cancel"});
    }
    
    if (flags & 8) {
      chips.push({title: "Published", icon: "check_circle"});
    }

    if (flags & 1) {
      if (flags == 1) {
        chips.push({title: "Pending initial review", icon: "pending"});
      } else {
        chips.push({title: "Pending update", icon: "pending_actions"});
      }
    }

    if (flags & 16 && !(flags & 8)) {
      chips.push({title: "Previously published", icon: "history_toggle_off"});
    }

    return chips;
  }

  toggleFilters(flag: number) {
    if (this.isSet(this.filters, flag)) {
      this.filters &= ~flag;
    } else {
      this.filters |= flag;
    }
    this.page = 1;
    this.hasMoreData = true;
    this.isInitialLoading = true;
    this.saveState();
    this.loadScripts(true);
  }

  toggleIncludeDrafts() {
    this.includeDrafts = !this.includeDrafts;
    this.page = 1;
    this.hasMoreData = true;
    this.isInitialLoading = true;
    this.saveState();
    this.loadScripts(true);
  }

  isSet(flags: number, flag: number) {
    return (flags & flag) == flag;
  }

  updateSort(sortBy: string) {
    if (this.sortBy === sortBy) {
      // Toggle sort order if same field
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new sort field with default order
      this.sortBy = sortBy;
      this.sortOrder = 'desc';
    }
    this.page = 1;
    this.hasMoreData = true;
    this.isInitialLoading = true;
    this.saveState();
    this.loadScripts(true);
  }

  getSortLabel(): string {
    switch (this.sortBy) {
      case 'rating': return 'Rating';
      case 'views': return 'Views';
      case 'age': return 'Age';
      case 'name': return 'Name';
      case 'id': return 'ID';
      default: return 'ID';
    }
  }

  getActiveFilterCount(): number {
    let count = 0;
    if (this.isSet(this.filters, 1)) count++;
    if (this.isSet(this.filters, 2)) count++;
    if (this.isSet(this.filters, 8)) count++;
    if (this.isSet(this.filters, 4)) count++;
    if (this.isSet(this.filters, 16)) count++;
    return count;
  }

  loadMore() {
    if (!this.isLoading && this.hasMoreData) {
      this.loadScripts();
    }
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
    if (this.scriptItems) {
      this.scriptItems.changes.subscribe(() => {
        this.observeLastItem();
      });
    }
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMoreData && !this.isLoading) {
            this.loadMore();
          }
        });
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1
      }
    );
    this.observeLastItem();
  }

  private observeLastItem() {
    if (!this.intersectionObserver) return;
    // Disconnect previous observed targets
    this.intersectionObserver.disconnect();
    const items = this.scriptItems?.toArray();
    if (items && items.length > 0) {
      const lastEl = items[items.length - 1].nativeElement as Element | null | undefined;
      if (lastEl && lastEl.nodeType === Node.ELEMENT_NODE) {
        this.intersectionObserver.observe(lastEl);
      }
    }
  }
  private saveState() {
    const state = {
      filters: this.filters,
      includeDrafts: this.includeDrafts,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      currentQuery: this.currentQuery
    };
    sessionStorage.setItem('admin_scripts_state', JSON.stringify(state));
  }

  private loadState() {
    const state = sessionStorage.getItem('admin_scripts_state');
    if (state) {
      const parsed = JSON.parse(state);
      this.filters = parsed.filters ?? 0;
      this.includeDrafts = parsed.includeDrafts ?? false;
      this.sortBy = parsed.sortBy ?? 'id';
      this.sortOrder = parsed.sortOrder ?? 'desc';
      this.currentQuery = parsed.currentQuery ?? '';
    }
  }
}
